import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { LoadingController, NavController, NavParams } from "ionic-angular";
import Company from "models/company-details/company";
import { commonVar } from "../../../../providers/common-var/common";
import { CompanyManagementService } from "../../../../providers/company-management-service/company-management-service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

/**
 * CREATE COMPANY (CO-3174).
 *
 * The Companies grid had no create surface at all: addCompany() was commented out, there was
 * no button, and CompanyManagementService.createCompany() — which has existed all along — was
 * called from nowhere in the app.
 *
 * This is a PLAIN @Component pushed by class, matching CreateFacilityPage and CreatePayerPage
 * exactly: same page shell (company-navigation side menu → ion-card → back button → header),
 * declared/exported/entryComponents in CompanyPageModule, reached from an ion-fab "+" on the
 * grid, and it closes with navCtrl.pop().
 *
 * It replaces the modal that shipped first. The modal was wrong twice over:
 *   * it did not match ANY other add flow here — every sibling grid (Facilities, Payers) pushes
 *     a create PAGE, and modals are reserved for archive/delete confirmations;
 *   * it did not actually work. ModalController is a ROOT-level provider and resolves component
 *     factories from the ROOT module, so a component declared only in a lazy page module was
 *     invisible to it and the deployed build threw
 *         "No component factory found for AddCompanyModal"
 *     the moment the button was clicked. Every unit test passed anyway, because they
 *     instantiate the class directly and never go through ModalController.
 * Pushing a page by class sidesteps that entirely — the factory lives in CompanyPageModule's
 * entryComponents, exactly like its two siblings.
 */
@Component({
  selector: "create-company",
  templateUrl: "create-company.html",
})
export class CreateCompanyPage implements OnInit {
  form: FormGroup;
  submitted: boolean = false;
  cardHeader: string = "Add Company";

  constructor(
    private formBuilder: FormBuilder,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    private companyManagementService: CompanyManagementService,
    private utilityService: UtilityService,
    private loader: LoadingController
  ) {}

  ngOnInit() {
    this.cardHeader = this.navParams.get("title") || "Add Company";
    this.form = this.formBuilder.group({
      name: ["", [Validators.required, Validators.maxLength(75)]],
      contactFirstName: ["", [Validators.maxLength(75)]],
      contactLastName: ["", [Validators.maxLength(75)]],
      email: [
        "",
        [Validators.pattern(commonVar.EMAIL_REGEX), Validators.maxLength(75)],
      ],
      phone: [""],
      website: ["", [Validators.maxLength(75)]],
      address1: ["", [Validators.required, Validators.maxLength(50)]],
      address2: ["", [Validators.maxLength(50)]],
      city: ["", [Validators.required, Validators.maxLength(50)]],
      state: ["", [Validators.required, Validators.maxLength(2)]],
      zipCode: ["", [Validators.required, Validators.maxLength(10)]],
      country: ["United States", [Validators.maxLength(50)]],
    });
  }

  invalid(control: string): boolean {
    const c = this.form.get(control);
    return !!c && c.invalid && (c.dirty || c.touched || this.submitted);
  }

  /**
   * Flat form → the nested CompanyDto the API expects.
   *
   * A separate, directly unit-testable method: the create payload IS the contract with the
   * backend, and the locations equivalent is exactly where CO-3159 silently lost the user's
   * work.
   */
  toDto(): Company {
    const v = this.form.value;
    const phones = v.phone ? [{ number: v.phone, type: "work", ext: "" }] : [];

    return {
      // NO companyId — the SERVER mints it. The backend used to copy companyId off the request
      // body, so a created company was stored with companyId:"" and could never be read back
      // or updated (fixed in company.go, guarded by company_create_id_test.go).
      name: (v.name || "").trim(),
      website: v.website || "",
      emailDomainFilters: [],
      contact: {
        firstName: (v.contactFirstName || "").trim(),
        lastName: (v.contactLastName || "").trim(),
        middleInitial: "",
        prefix: "",
        suffix: "",
        email: v.email || "",
        phones: phones,
      },
      address: {
        address1: v.address1 || "",
        address2: v.address2 || "",
        city: v.city || "",
        state: (v.state || "").toUpperCase(),
        zipCode: v.zipCode || "",
        country: v.country || "",
        county: "",
      },
    } as any;
  }

  save(): void {
    this.submitted = true;
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((k) =>
        this.form.get(k).markAsTouched()
      );
      return;
    }

    const spinner = this.loader.create({ content: "Creating company..." });
    spinner.present();

    this.companyManagementService.createCompany(this.toDto()).subscribe(
      () => {
        spinner.dismiss();
        // Pop ONLY on success — the grid reloads in ionViewWillEnter. Popping on failure would
        // throw away everything the user typed and look like the save had worked.
        this.navCtrl.pop();
      },
      (err) => {
        spinner.dismiss();
        this.utilityService.presentErrorToast(
          err,
          "Failed to create the company"
        );
      }
    );
  }

  /** Back / Cancel — matches CreateFacilityPage.close(). */
  close(): void {
    this.navCtrl.pop();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/company-management/create-company/create-company.ts